import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, Sparkles } from "lucide-react";
import { submitInquiry } from "@/lib/api";
import { toast } from "sonner";

const PROJECT_TYPES = [
  "Chopping board",
  "Chess / Game board",
  "Dart cabinet",
  "Dining table",
  "Coffee / Side table",
  "Wedding or gift piece",
  "Corporate / Bulk gift order",
  "Signage / Engraving",
  "Other",
];

const TIMBER_OPTIONS = [
  "Not sure — recommend something",
  "Macrocarpa",
  "Rimu",
  "Matai",
  "American Walnut",
  "European Oak",
  "Mixed / Contrast",
];

const BUDGETS = [
  "Under $250",
  "$250 – $500",
  "$500 – $1,000",
  "$1,000 – $2,500",
  "$2,500 – $5,000",
  "$5,000+",
  "Open / Not sure",
];

const TIMELINES = [
  "No rush",
  "Within 4 weeks",
  "Within 6 weeks",
  "Within 3 months",
  "Specific date (mention below)",
];

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  project_type: "",
  timber: "",
  dimensions: "",
  budget: "",
  timeline: "",
  details: "",
};

function buildMessage(form) {
  return [
    `Project type: ${form.project_type || "—"}`,
    `Preferred timber: ${form.timber || "—"}`,
    `Approx. dimensions: ${form.dimensions || "—"}`,
    `Budget: ${form.budget || "—"}`,
    `Timeline: ${form.timeline || "—"}`,
    "",
    "Details:",
    form.details || "—",
  ].join("\n");
}

const inputClass =
  "mt-2 bg-transparent rounded-none border-0 border-b border-[#A89F91]/50 focus-visible:ring-0 focus-visible:border-[#2C2421] px-0";
const triggerClass =
  "mt-2 bg-transparent rounded-none border-0 border-b border-[#A89F91]/50 focus:ring-0 focus:border-[#2C2421] px-0 h-10";
const labelClass = "eyebrow !text-[#6F4E37]";

function FormBody({ onSubmitted, preset }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    project_type: preset?.project_type || "",
    details: preset?.details || "",
  }));
  const [submitting, setSubmitting] = useState(false);

  // Reset form when preset changes (e.g. user toggles addons then reopens)
  useEffect(() => {
    setForm((f) => ({
      ...f,
      project_type: preset?.project_type ?? f.project_type,
      details: preset?.details ?? f.details,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset?.project_type, preset?.details]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_type || !form.details) {
      toast.error("Name, email, project type and details are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitInquiry({
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: `Commission enquiry — ${form.project_type}`,
        message: buildMessage(form),
        project_type: "commission",
      });
      toast.success("Brief sent to the workshop. We'll reply within 48 hours.");
      onSubmitted(form.email);
      setForm(EMPTY);
    } catch (err) {
      toast.error("Couldn't send right now. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form data-testid="commission-form" onSubmit={handleSubmit}>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelClass}>Name *</label>
          <Input
            data-testid="commission-name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className={inputClass}
            placeholder="Your name"
          />
        </div>
        <div>
          <label className={labelClass}>Email *</label>
          <Input
            data-testid="commission-email"
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            className={inputClass}
            placeholder="you@example.com"
          />
        </div>
        <div>
          <label className={labelClass}>Phone</label>
          <Input
            data-testid="commission-phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            className={inputClass}
            placeholder="+64…"
          />
        </div>
        <div>
          <label className={labelClass}>Project type *</label>
          <Select value={form.project_type} onValueChange={(v) => update("project_type", v)}>
            <SelectTrigger data-testid="commission-project-type" className={triggerClass}>
              <SelectValue placeholder="Choose…" />
            </SelectTrigger>
            <SelectContent>
              {PROJECT_TYPES.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className={labelClass}>Preferred timber</label>
          <Select value={form.timber} onValueChange={(v) => update("timber", v)}>
            <SelectTrigger data-testid="commission-timber" className={triggerClass}>
              <SelectValue placeholder="Choose…" />
            </SelectTrigger>
            <SelectContent>
              {TIMBER_OPTIONS.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className={labelClass}>Approx. dimensions</label>
          <Input
            data-testid="commission-dimensions"
            value={form.dimensions}
            onChange={(e) => update("dimensions", e.target.value)}
            className={inputClass}
            placeholder='e.g. 1800 × 900 × 750mm'
          />
        </div>

        <div>
          <label className={labelClass}>Budget</label>
          <Select value={form.budget} onValueChange={(v) => update("budget", v)}>
            <SelectTrigger data-testid="commission-budget" className={triggerClass}>
              <SelectValue placeholder="Choose…" />
            </SelectTrigger>
            <SelectContent>
              {BUDGETS.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className={labelClass}>Timeline</label>
          <Select value={form.timeline} onValueChange={(v) => update("timeline", v)}>
            <SelectTrigger data-testid="commission-timeline" className={triggerClass}>
              <SelectValue placeholder="Choose…" />
            </SelectTrigger>
            <SelectContent>
              {TIMELINES.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6">
        <label className={labelClass}>Tell us about your project *</label>
        <Textarea
          data-testid="commission-details"
          value={form.details}
          onChange={(e) => update("details", e.target.value)}
          rows={6}
          className="mt-2 bg-transparent rounded-none border border-[#A89F91]/40 focus-visible:ring-0 focus-visible:border-[#2C2421] resize-none"
          placeholder="Describe what you'd like made, who it's for, any special features (juice groove, engraving, live edge), and any reference images you can share later."
        />
      </div>

      <button
        type="submit"
        data-testid="commission-submit"
        disabled={submitting}
        className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#2C2421] text-[#FDFBF7] px-7 py-4 text-sm font-medium hover:bg-[#4A3C38] transition-colors disabled:opacity-60"
      >
        {submitting ? "Sending…" : (<><Send size={14}/> Send brief to the workshop</>)}
      </button>

      <p className="mt-4 text-xs text-[#6F4E37] leading-relaxed">
        We read every brief personally. You'll hear back within 48 hours at the email above.
      </p>
    </form>
  );
}

export function CommissionFormInline({ preset }) {
  const [sentEmail, setSentEmail] = useState(null);

  if (sentEmail) {
    return (
      <div data-testid="commission-inline-success" className="text-center py-16">
        <div className="font-serif text-4xl md:text-5xl mb-4">Thank you.</div>
        <p className="text-[#6F4E37] max-w-md mx-auto leading-relaxed">
          Your brief is with the workshop. Expect a personal reply within 48 hours at <span className="text-[#2C2421]">{sentEmail}</span>.
        </p>
        <button
          data-testid="commission-inline-new"
          onClick={() => setSentEmail(null)}
          className="mt-8 text-sm tracking-wide link-underline"
        >
          Send another brief
        </button>
      </div>
    );
  }

  return <FormBody onSubmitted={setSentEmail} preset={preset} />;
}

export default function CommissionForm({ open, onOpenChange, preset }) {
  const [sentEmail, setSentEmail] = useState(null);

  const reset = () => setSentEmail(null);

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 200);
      }}
    >
      <SheetContent
        side="right"
        data-testid="commission-form-sheet"
        className="w-full sm:max-w-[560px] p-0 bg-[#FDFBF7] border-l border-[#E5E0D8] flex flex-col"
      >
        <SheetHeader className="px-8 pt-8 pb-5 border-b border-[#E5E0D8] text-left">
          <SheetTitle className="font-serif text-3xl font-normal flex items-center gap-2">
            <Sparkles size={18} className="text-[#6F4E37]" />
            Start a Commission
          </SheetTitle>
          <SheetDescription className="text-sm text-[#6F4E37] leading-relaxed">
            Share your idea — the workshop will review and quote within 48 hours.
          </SheetDescription>
        </SheetHeader>

        {sentEmail ? (
          <div data-testid="commission-form-success" className="flex-1 flex flex-col items-center justify-center px-8 text-center">
            <div className="font-serif text-4xl mb-4">Thank you.</div>
            <p className="text-[#6F4E37] max-w-sm leading-relaxed">
              Your brief is with the workshop. Expect a personal reply within 48 hours at <span className="text-[#2C2421]">{sentEmail}</span>.
            </p>
            <button
              data-testid="commission-form-new"
              onClick={reset}
              className="mt-10 text-sm tracking-wide link-underline"
            >
              Send another brief
            </button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto scroll-soft px-8 py-6">
            <FormBody onSubmitted={setSentEmail} preset={preset} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
